import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Typography from "../components/v2/Typography";
import Button from "../components/v2/Button";
import { ArrowRight, ArrowRightCircle } from "lucide-react";
import ServicesHeader from "../atoms/ServicesHeader";
import ContactSection from "../atoms/Contact";
import BlogSection from "../atoms/BlogPreview";
import Footer from "../atoms/Footer";
import Faq from "../atoms/Faq";
import useApp from "../hooks/useApp";
import Input from "../components/v2/Input";
import Textarea from "../components/v2/TextArea";
import ContactForm from "../atoms/ContactForm";

const ServiceShowcaseR = () => {
    return (
        <section className="service-showcase right-aligned">
            <div className="inner">
                <div className="service-image">
                    <img src="/images/services/domain-broker.jpg" alt="Domain Acquisition Services" />
                </div>
                <div className="service-content">
                    <div className="service-text">
                        <div className="service-heading">
                            <Typography variant="h1">Premium Domain Acquisition</Typography>
                        </div>
                        <div className="service-para">
                            <Typography variant="para">From startups seeking their perfect brand name to Fortune 500 companies expanding their digital portfolio, NameConnect delivers strategic domain acquisition services tailored to your unique goals.</Typography>
                            <Typography variant="para">Our comprehensive approach includes confidential acquisitions, investment consulting, portfolio optimization, and strategic partnership facilitation—all designed to maximize your digital asset value.</Typography>
                        </div>
                    </div>
                    <div className="service-controls">
                        <Button isLink={true} linkProps={{to:"/contact"}} variant='primary'>
                            <Typography variant={'button'}>Start Your Acquisition</Typography>
                        </Button>
                        <Button isLink={true} linkProps={{to:"/domains-for-sale"}} variant='secondary'><Typography variant={'button'}>Browse Premium Domains</Typography></Button>
                    </div>
                </div>
            </div>
        </section>
    );
};

const DomainAcquisitionService = () => {
    const [faqData, setFaqData] = useState([]);
    const {allFaqList} = useApp();
    const [processData, setProcessData] = useState([
        {
            q: "Discovery Consultation",
            a: "We begin with an in-depth consultation to understand your business objectives, target market, and domain requirements. This strategic foundation ensures we pursue domains that align perfectly with your brand vision."
        },
        {
            q: "Strategic Planning & Research",
            a: "Our team develops a comprehensive acquisition strategy, identifying high-value targets and analyzing market conditions. We leverage proprietary data and industry insights to create your optimal approach."
        },
        {
            q: "Professional Outreach",
            a: "Using our extensive network and proven communication strategies, we initiate discreet contact with domain owners, maintaining your anonymity while establishing productive negotiations."
        },
        {
            q: "Expert Negotiation",
            a: "Our seasoned brokers handle all negotiations with skill and precision, leveraging market knowledge and negotiation expertise to secure favorable terms while protecting your interests."
        },
        {
            q: "Secure Transfer & Escrow",
            a: "We manage the entire transfer process through trusted escrow services, ensuring complete security for both parties. Your domain is safely transferred with full legal protection and verification."
        }
    ]);
    

    // useEffect(() => {
    //     // Manual URL, replace later
    //     fetch('manual-url-for-faq')
    //         .then(res => res.json())
    //         .then(data => setFaqData(data))
    //         .catch(() => {
    //             setFaqData([
    //                 { q: "What is WHOIS domain lookup?", a: "Short, simple answer…" },
    //                 { q: "What does the WHOIS database contain?", a: "Another answer…" }
    //             ]);
    //         });
    // }, []);

    return (
        <>
            <ServicesHeader active={"/services/domain-acquisition"} />
            <ServiceShowcaseR />

            <section className="service-intro-showcase">
                <div className="inner">
                    <div className="service-content">
                        {/* Section 1 */}
                        <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">Strategic Domain Acquisition Services</Typography>
                            </div>
                            <div className="service-para">
                                <p>Buy. Sell. Acquire. Confidentially and Strategically.</p>
                                <p>At NameConnect, we help individuals, startups, and global enterprises buy or sell high-value domain names with confidentiality, precision, and care.</p>
                                <ul>
                                    <li>A business looking to acquire your exact brand name,</li>
                                    <li>An investor aiming to unlock the value of your domain,</li>
                                    <li>An entrepreneur ready to make your first digital investment — we’re here to help.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 2 */}
                        <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">Why Use a Domain Broker?</Typography>
                            </div>
                            <div className="service-para">
                                <ul className="with-points">
                                    <li>You want to buy a domain that’s already taken.</li>
                                    <li>You want to sell a domain and maximize its value.</li>
                                    <li>You need to keep your identity hidden during the transaction.</li>
                                    <li>You’re short on time or don’t have industry contacts.</li>
                                    <li>You want expert negotiation support.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 3 */}
                        <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">What We Do</Typography>
                            </div>
                            <div className="service-para">
                                <p>💼 <strong>Domain Sales</strong>: We market premium domains to qualified buyers fast.</p>
                                <p>🔍 <strong>Domain Acquisition</strong>: We discreetly approach domain owners, negotiate, and secure names for you.</p>
                                <p>🧠 <strong>Consulting & Valuation</strong>: Real market data, accurate valuations, no guesswork.</p>
                            </div>
                        </div>

                        {/* Section 4 */}
                        <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">What Sets NameConnect Apart?</Typography>
                            </div>
                            <div className="service-para">
                                <ul className="with-points">
                                    <li>Proactive outreach to find buyers or sellers.</li>
                                    <li>Discreet transactions to protect your identity.</li>
                                    <li>Expert-level negotiation to close deals.</li>
                                    <li>Strong industry connections worldwide.</li>
                                    <li>Commission-based success — we win when you do.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 5 */}
                        <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">Our Process</Typography>
                            </div>
                            <div className="service-para">
                                {/* <ul className="with-points">
                                    <li>Consultation – Understand your goals.</li>
                                    <li>Strategy – Plan, target, outreach.</li>
                                    <li>Outreach – Direct contact with buyers/sellers.</li>
                                    <li>Negotiation – We handle the deal.</li>
                                    <li>Escrow & Transfer – 100% secure domain handover.</li>
                                </ul> */}

                                <Faq data={processData} useNumber={true} noBorder={true} leftAligned={true} />
                            </div>
                        </div>

                        {/* Section 6 */}
                        <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">Fees & Commission</Typography>
                            </div>
                            <div className="service-para">
                                <p>Selling? 15% commission on final price, $1,000 minimum.</p>
                                <p>Buying? Flat or percentage fee — based on value and work involved.</p>
                                <p>No upfront fees unless agreed for extra marketing.</p>
                            </div>
                        </div>

                        {/* Section 7 */}
                        <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">Secure Transactions</Typography>
                            </div>
                            <div className="service-para">
                                <p>We use trusted services like Escrow.com and PayPal for safe, verified transactions.</p>
                                <p>Your privacy and security are always our top priority.</p>
                            </div>
                        </div>

                        {/* Section 8 */}
                        <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">Ready to Buy or Sell a Domain?</Typography>
                            </div>
                            <div className="service-para">
                                <p>Let’s talk. Whether one domain or a whole portfolio, we’ll handle it from start to finish.</p>
                                <Button variant='primary'>
                                    <Typography variant={'button'}>Contact a Broker</Typography>
                                </Button>
                            </div>
                        </div>

                        {/* FAQ from fetch */}
                        {/* <div className="service-text">
                            <div className="service-heading">
                                <Typography variant="h1">FAQs</Typography>
                            </div>
                            <div className="service-para">
                                <Faq data={faqData} useNumber={true} noBorder={true} leftAligned={true} />
                            </div>
                        </div> */}
                    </div>

                    <div className="service-form">
                        <ContactForm />
                    </div>
                </div>
            </section>

            <Faq data={allFaqList["services/domain-brokering"]} noBorder={false} leftAligned={false} showHeader={true} />
            <ContactSection />
            <BlogSection />
        </>
    );
};

export default DomainAcquisitionService;
